import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOSchema from '@/components/feature/SEOSchema';
import ScrollToTop from '@/components/feature/ScrollToTop';
import FeaturedComic from './components/FeaturedComic';
import ComicCard from './components/ComicCard';
import {
  categories,
  featuredComics,
  hotComics,
  allComics,
} from '@/mocks/comics';

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slideIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const slides = featuredComics.slice(0, 4);

  useEffect(() => {
    slideIntervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => {
      if (slideIntervalRef.current) clearInterval(slideIntervalRef.current);
    };
  }, [slides.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    if (slideIntervalRef.current) clearInterval(slideIntervalRef.current);
    slideIntervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
  };

  const latestUpdates = [...allComics]
    .sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime())
    .slice(0, 12);

  const topRated = [...allComics]
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 8);

  return (
    <>
      <ScrollToTop />
      <SEOSchema type="WebSite" />
      <SEOSchema type="Organization" />
      <SEOSchema type="WebPage" />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative w-full overflow-hidden" aria-label="精选推荐">
            <div className="relative h-[420px] md:h-[600px]">
              {slides.map((comic, index) => (
                <div
                  key={comic.id}
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
                  }`}
                >
                  <FeaturedComic
                    id={comic.id}
                    title={comic.title}
                    slug={comic.slug}
                    cover={comic.cover}
                    description={comic.description}
                    author={comic.author}
                    status={comic.status}
                    latestChapter={comic.latestChapter}
                    rating={comic.rating}
                    ratingCount={comic.ratingCount}
                  />
                </div>
              ))}

              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex gap-2">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${
                      index === currentSlide
                        ? 'bg-primary-500 w-6'
                        : 'bg-background-50/50 hover:bg-background-50/80'
                    }`}
                    aria-label={`第 ${index + 1} 张`}
                  />
                ))}
              </div>
            </div>
          </section>

          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14" aria-label="漫画分类">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">
                探索漫画世界
              </h2>
              <p className="text-sm text-foreground-500 mt-2">
                十二大分类，海量漫画任你挑选
              </p>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 md:gap-4">
              {categories.filter(c => c.slug !== 'all').slice(0, 12).map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-2 p-4 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-300 group"
                >
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors">
                    <i className={`${cat.icon} text-lg md:text-xl`}></i>
                  </div>
                  <span className="text-xs md:text-sm text-foreground-700 font-medium whitespace-nowrap">
                    {cat.name}
                  </span>
                  <span className="text-xs text-foreground-400">{cat.count}部</span>
                </Link>
              ))}
            </div>
          </section>

          <section className="bg-background-100 py-10 md:py-14" aria-label="热门排行">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">
                    热门排行
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">
                    大家都在看的热门漫画
                  </p>
                </div>
                <Link
                  to="/category/all"
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap flex items-center gap-1"
                >
                  查看全部 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
                {topRated.map((comic, index) => (
                  <div key={comic.id} className="relative">
                    {index < 3 && (
                      <div className="absolute -top-2 -left-2 z-10 w-7 h-7 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-xs font-bold">
                        {index + 1}
                      </div>
                    )}
                    <ComicCard
                      id={comic.id}
                      title={comic.title}
                      slug={comic.slug}
                      cover={comic.cover}
                      author={comic.author}
                      category={comic.category}
                      status={comic.status}
                      latestChapter={comic.latestChapter}
                      rating={comic.rating}
                      views={comic.views}
                      shortDescription={comic.shortDescription}
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14" aria-label="推荐漫画">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">
                  编辑推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">
                  精选优质漫画，本本都是精品
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {hotComics.map((comic) => (
                <ComicCard
                  key={comic.id}
                  id={comic.id}
                  title={comic.title}
                  slug={comic.slug}
                  cover={comic.cover}
                  author={comic.author}
                  category={comic.category}
                  status={comic.status}
                  latestChapter={comic.latestChapter}
                  rating={comic.rating}
                  views={comic.views}
                  shortDescription={comic.shortDescription}
                />
              ))}
            </div>
          </section>

          <section className="bg-background-100 py-10 md:py-14" aria-label="最新更新">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">
                    最新更新
                  </h2>
                  <p className="text-sm text-foreground-500 mt-1">
                    新鲜出炉的漫画章节
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {latestUpdates.map((comic) => (
                  <ComicCard
                    key={comic.id}
                    id={comic.id}
                    title={comic.title}
                    slug={comic.slug}
                    cover={comic.cover}
                    author={comic.author}
                    category={comic.category}
                    status={comic.status}
                    latestChapter={comic.latestChapter}
                    rating={comic.rating}
                    views={comic.views}
                    shortDescription={comic.shortDescription}
                  />
                ))}
              </div>
            </div>
          </section>

          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14" aria-label="为什么选择虫虫动漫">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">
                为什么选择虫虫动漫？
              </h2>
              <p className="text-sm text-foreground-500 mt-2">
                致力于打造最好的免费漫画阅读体验
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-6 bg-background-50 rounded-lg border border-background-200/70">
                <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                  <i className="ri-book-open-line text-2xl"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">海量漫画</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  收录超过1200部精选漫画，涵盖十二大热门分类，总有你喜欢的类型。
                </p>
              </div>
              <div className="text-center p-6 bg-background-50 rounded-lg border border-background-200/70">
                <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-500">
                  <i className="ri-currency-line text-2xl"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">完全免费</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  所有漫画内容完全免费阅读，无需付费，无需订阅，打开即看。
                </p>
              </div>
              <div className="text-center p-6 bg-background-50 rounded-lg border border-background-200/70">
                <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                  <i className="ri-smartphone-line text-2xl"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">下拉式阅读</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  流畅的下拉式滚动浏览体验，无需翻页，沉浸式阅读更畅快。
                </p>
              </div>
              <div className="text-center p-6 bg-background-50 rounded-lg border border-background-200/70">
                <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-500">
                  <i className="ri-refresh-line text-2xl"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">每日更新</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  热门漫画每日同步更新，追更不等待，第一时间阅读最新章节。
                </p>
              </div>
            </div>
          </section>

          <section className="bg-gradient-to-r from-primary-500 to-accent-500 py-12 md:py-16" aria-label="CTA">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 text-center">
              <h2 className="text-2xl md:text-3xl font-heading text-background-50 mb-3">
                开启你的免费漫画之旅
              </h2>
              <p className="text-sm text-background-50/80 mb-6 max-w-lg mx-auto">
                海量漫画全部免费，随时随地畅享阅读乐趣
              </p>
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 px-8 py-3 bg-background-50 text-primary-500 rounded-full font-medium text-sm hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                立即开始阅读 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}